import "../ResourceCategory/ResourceCategories.css";
import "../ManagementDialog.css";
import "./PolicyProfiles.css";
import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Input } from "../../ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../ui/popover";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "../../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Check, ChevronsUpDown, FileCog, Plus, Settings, Users, FileText, Trash2, Monitor, Search } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "../../ui/command";
import { myLocalStorage } from "../../StorageHelper";
import { copyProfile, createPendingProfile, deleteCopiedProfiles, fetchPendingProfiles, getProfiles, updateGlobalOperatingMode, updatePolicyMode, updatePolicyTime } from "./Api/policyprofileapi";
import { PolicyProfiles } from "./interface";
import { normalizeProfileOs, PolicyProfileOsTab } from "./helper";
import { useNavigate } from "react-router-dom";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";
import type { IconType } from "react-icons";
import CardPagination from "../CardPagination";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
} from "../../../utils/listPaginationState";

const OS_TABS: { value: PolicyProfileOsTab; label: string; dialogLabel: string; icon: IconType }[] = [
    { value: "windows", label: "Windows", dialogLabel: "Windows", icon: FaWindows },
    { value: "linux", label: "Linux", dialogLabel: "Linux", icon: FaLinux },
    { value: "mac", label: "Mac", dialogLabel: "macOS", icon: FaApple },
];

const defaultTimeOptions = [
    { value: 60, label: "60 min" },
    { value: 120, label: "120 min" },
    { value: 180, label: "180 min" },
    { value: 240, label: "240 min" }
];

type PolicyProfilesListState = {
    activeTab?: string;
    searchTerm?: string;
    page?: number;
    pageWindows?: number;
    pageLinux?: number;
    pageMac?: number;
};

export default function PolicyProfileDetails() {
    const savedListState = readPersistedListState<PolicyProfilesListState>(
        LIST_STATE_KEYS.policyProfiles,
    );
    const DESCRIPTION_MAX_LENGTH = 25;

    const [profiles, setProfiles] = useState<PolicyProfiles[]>([]);
    const [profilesLoaded, setProfilesLoaded] = useState(false);
    const [activeTab, setActiveTab] = useState<PolicyProfileOsTab>(
        parsePersistedString(savedListState.activeTab, "windows") as PolicyProfileOsTab,
    );
    const [searchTerm, setSearchTerm] = useState(
        parsePersistedString(savedListState.searchTerm, ""),
    );
    const [pagesByTab, setPagesByTab] = useState<Record<PolicyProfileOsTab, number>>({
        windows: parsePersistedNumber(
            savedListState.pageWindows ?? savedListState.page,
            1,
        ),
        linux: parsePersistedNumber(savedListState.pageLinux, 1),
        mac: parsePersistedNumber(savedListState.pageMac, 1),
    });
    const currentPage = pagesByTab[activeTab];
    const itemsPerPage = 5;
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem('latestTenant')?.tenantName || '';
    const tenant = myLocalStorage.getItem("latestTenant");

    // Create profile dialog state
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");
    const [selectedOS, setSelectedOS] = useState<'windows' | 'linux' | 'mac'>('windows');
    const [selectedCloneProfile, setSelectedCloneProfile] = useState("");
    const [openCloneSelect, setOpenCloneSelect] = useState(false);


    const navigate = useNavigate();

    const setCurrentPage = (value: number | ((prev: number) => number)) => {
        setPagesByTab((prev) => {
            const prevPage = prev[activeTab];
            const nextPage = typeof value === "function" ? value(prevPage) : value;
            return { ...prev, [activeTab]: nextPage };
        });
    };

    useEffect(() => {
        writePersistedListState<PolicyProfilesListState>(LIST_STATE_KEYS.policyProfiles, {
            activeTab,
            searchTerm,
            pageWindows: pagesByTab.windows,
            pageLinux: pagesByTab.linux,
            pageMac: pagesByTab.mac,
        });
    }, [activeTab, searchTerm, pagesByTab]);

    const persistListState = () => {
        writePersistedListState<PolicyProfilesListState>(LIST_STATE_KEYS.policyProfiles, {
            activeTab,
            searchTerm,
            pageWindows: pagesByTab.windows,
            pageLinux: pagesByTab.linux,
            pageMac: pagesByTab.mac,
        });
    };

    const handleOperatingModeChange = async (profileId: number, newMode: string) => {
        setProfiles(prev =>
            prev.map(profile =>
                profile.id === profileId
                    ? { ...profile, mode: newMode as PolicyProfiles['mode'] }
                    : profile
            )
        );

        let response = await updatePolicyMode(profileId, tenantName, newMode)
        if (response) {
            toast({ title: "Operating mode updated successfully" });
        }
    };

    const handleMode = async (newMode: string) => {
        let response = await updateGlobalOperatingMode(tenantName, newMode);
        if (response) {
            toast({ title: "Operating mode updated successfully" });
            const tenant = myLocalStorage.getItem("latestTenant");

            if (tenant) {
                const parsedTenant = typeof tenant === "string" ? JSON.parse(tenant) : tenant;

                const updatedTenant = { ...parsedTenant, mode: newMode };

                myLocalStorage.setItem("latestTenant", updatedTenant);
            }
            fetchProfiles(tenantName)

        }

    }

    const handleDefaultTimeChange = async (profileId: number, newTime: string) => {
        setProfiles((prev = []) =>
            prev.map(profile =>
                profile.id === profileId
                    ? { ...profile, defaultTime: parseInt(newTime) }
                    : profile
            )
        );
        try {
            await updatePolicyTime(profileId, Number(newTime));
            toast({ title: "Default time updated successfully" });
        } catch (e) {

        }

    };

    const handleDelete = async (profileId: number) => {
        const id = profileId;
        const profile = profiles?.find(p => p.id === id);

        if (profile?.isCopied && profile?.canBeDeleted) {
            setProfiles((prev = []) => prev.filter(p => p.id !== id));
            await deleteCopiedProfiles(profileId);
            toast({ title: `Profile "${profile.name}" deleted successfully` });
        } else {
            toast({ title: `Profile "${profile?.name}" is configured in the system and cannot be deleted` });
        }
    };

    const handleAction = (action: string, profileName: string, id: number) => {
        if (action === "Create new profile") {
            setShowCreateDialog(true);
        } else if (action === "Details") {
            localStorage.setItem('currentProfileName', profileName);
            persistListState();
            navigate(`/policyProfiles/resources/${id}/${activeTab}`, { state: { profileName } });
        } else if (action === "Categories") {
            const profile = profiles?.find(p => p.name === profileName);
            const osType = profile?.os || "windows";
            localStorage.setItem('currentProfileName', profileName);
            persistListState();
            navigate(
                `/policyProfiles/categories/${profile?.id}/${activeTab}/${osType}`,
                { state: { profileName } }
            );
        } else if (action === "Settings") {
            const profile = profiles?.find(p => p.name === profileName);
            const osType = profile?.os || "windows";
            localStorage.setItem('currentProfileName', profileName);
            persistListState();
            navigate(
                `/policyProfiles/settings/${profile?.id}/${activeTab}/${osType}`,
                { state: { profileName } }
            );
        } else {
            // No action
        }
    };

    // Get available profiles for cloning based on selected OS
    const getAvailableProfilesForCloning = () => {
        return profiles.filter(profile => normalizeProfileOs(profile.os) === selectedOS);
    };

    // Handle create profile form submission
    const handleCreateProfile = async () => {
        if (!newProfileName.trim()) {
            toast({ title: "Please enter a profile name", variant: "destructive" });
            return;
        }

        if (!selectedCloneProfile) {
            toast({ title: "Please select a profile to clone from", variant: "destructive" });
            return;
        }

        const cloneFromProfile = profiles.find(p => p.id === Number(selectedCloneProfile));
        if (!cloneFromProfile) return;


        let response = await createPendingProfile(cloneFromProfile.id, newProfileName)

        if (response) {
            await fetchPendingProfiles(tenantName);
            await copyProfile(cloneFromProfile?.id, newProfileName);
        }

        // Reset form and close dialog
        setNewProfileName("");
        setSelectedOS('windows');
        setSelectedCloneProfile("");
        setOpenCloneSelect(false);
        setShowCreateDialog(false);
        fetchProfiles(tenantName)
    };

    // Reset clone profile when OS changes
    useEffect(() => {
        const availableProfiles = getAvailableProfilesForCloning();
        if (availableProfiles.length > 0) {
            // Look for original profile first (e.g., windows_original)
            availableProfiles.find(profile =>
                profile.name === `${selectedOS}_original`
            );
        }
    }, [selectedOS, profiles]);

    const filterProfilesForTab = (tab: PolicyProfileOsTab) =>
        profiles.filter(profile => {
            const matchesOS = normalizeProfileOs(profile.os) === tab;
            const matchesSearch = searchTerm === "" ||
                profile.name.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesOS && matchesSearch;
        });

    const filteredProfiles = filterProfilesForTab(activeTab);

    const totalPages = Math.max(1, Math.ceil(filteredProfiles.length / itemsPerPage));
    const safeCurrentPage = profilesLoaded
        ? Math.min(currentPage, totalPages)
        : currentPage;

    useEffect(() => {
        if (!profilesLoaded) return;
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [profilesLoaded, currentPage, totalPages, activeTab]);

    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const paginatedProfiles = filteredProfiles.slice(startIndex, startIndex + itemsPerPage);

    const handleTabChange = (value: string) => {
        setActiveTab(value as PolicyProfileOsTab);
        setSearchTerm("");
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };



    const fetchProfiles = async (tenantName: string) => {
        try {
            const response: PolicyProfiles[] = await getProfiles(tenantName);
            if (response) {
                setProfiles(response);
            }
        } finally {
            setProfilesLoaded(true);
        }
    }


    useEffect(() => {
        setProfilesLoaded(false);
        fetchProfiles(tenantName);
    }, [tenantName]);

    const getEffectiveMode = (operatingMode: string, mode: string) =>
        (operatingMode === "INHERITED_FROM_TENANT" ? mode : operatingMode).toLowerCase();

    const renderProfileCard = (profile: PolicyProfiles) => {
        const effectiveMode = getEffectiveMode(profile.mode, tenant.mode);

        return (
            <div key={profile.id} className="policy-row policy-grid" data-mode={effectiveMode}>
                <div className="policy-identity">
                    <div className="policy-name-line">
                        <h3 className="policy-name" data-testid={`text-profile-name-${profile.id}`}>
                            {profile.name}
                        </h3>
                        <span className="policy-badge" data-kind={profile.isCopied ? "custom" : "builtin"}>
                            {profile.isCopied ? "Custom" : "Built-in"}
                        </span>
                    </div>
                    <p className="policy-sub">
                        <span className="policy-mode-dot" data-mode={effectiveMode} aria-hidden="true" />
                        {effectiveMode}
                    </p>
                </div>

                <Select
                    value={profile.defaultTime.toString()}
                    onValueChange={(value) => handleDefaultTimeChange(profile.id, value)}
                >
                    <SelectTrigger
                        className="policy-select"
                        aria-label={`Default time for ${profile.name}`}
                        data-testid={`select-default-time-${profile.id}`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {defaultTimeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={profile.mode}
                    onValueChange={(value) => handleOperatingModeChange(profile.id, value)}
                >
                    <SelectTrigger
                        className="policy-select"
                        aria-label={`Operating mode for ${profile.name}`}
                        data-testid={`select-operating-mode-${profile.id}`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="AUDITING">AUDITING</SelectItem>
                        <SelectItem value="ENFORCING">ENFORCING</SelectItem>
                        <SelectItem value="DISABLED">DISABLED</SelectItem>
                        <SelectItem value="INHERITED_FROM_TENANT">INHERITED {`(${tenant.mode})`}</SelectItem>
                    </SelectContent>
                </Select>

                <div className="policy-actions">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAction("Details", profile.name, profile.id)}
                        className="policy-action no-default-hover-elevate"
                        data-testid={`button-details-${profile.id}`}
                    >
                        <FileText />
                        Resources
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAction("Categories", profile.name, profile.id)}
                        className="policy-action no-default-hover-elevate"
                        data-tone="violet"
                        data-testid={`button-categories-${profile.id}`}
                    >
                        <Users />
                        Categories
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            myLocalStorage.setItem("profilesDetails", profile)
                            handleAction("Settings", profile.name, profile.id)
                        }}
                        className="policy-action no-default-hover-elevate"
                        data-tone="slate"
                        data-testid={`button-settings-${profile.id}`}
                    >
                        <Settings />
                        Settings
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(profile.id)}
                        disabled={!profile.isCopied}
                        className="policy-action no-default-hover-elevate"
                        data-tone="danger"
                        data-icon-only="true"
                        aria-label={`Delete ${profile.name}`}
                        data-testid={`button-delete-${profile.id}`}
                    >
                        <Trash2 />
                    </Button>
                </div>
            </div>
        );
    };

    const renderProfilesList = (tab: PolicyProfileOsTab, emptyLabel: string) => {
        const tabPage = pagesByTab[tab];
        const tabProfiles = tab === activeTab
            ? paginatedProfiles
            : filterProfilesForTab(tab).slice(
                (tabPage - 1) * itemsPerPage,
                tabPage * itemsPerPage,
            );

        if (tabProfiles.length > 0) {
            return tabProfiles.map(renderProfileCard);
        }

        return (
            <div className="policy-empty">
                <span className="policy-empty-icon" aria-hidden="true">
                    <Monitor />
                </span>
                <p data-testid="text-no-profiles">
                    {searchTerm ? `No ${emptyLabel} profiles found matching "${searchTerm}"` : `No ${emptyLabel} profiles found`}
                </p>
            </div>
        );
    };

    const renderPagination = () => {
        if (filteredProfiles.length === 0) {
            return null;
        }

        return (
            <div className="policy-pagination">
                <span className="policy-pagination-summary">
                    Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredProfiles.length)} of {filteredProfiles.length} profiles
                </span>
                <CardPagination
                    label="Profile pages"
                    currentPage={safeCurrentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        );
    };

    const activeOsLabel = OS_TABS.find((tab) => tab.value === activeTab)?.label ?? "Windows";

    return (
        <div className="policy-profiles">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="policy-layout">
                <div className="category-toolbar">
                    <TabsList unstyled className="category-os-tabs">
                        {OS_TABS.map(({ value, label, icon: OsIcon }) => (
                            <TabsTrigger
                                key={value}
                                unstyled
                                value={value}
                                className="category-os-tab"
                                data-testid={`tab-${value}`}
                            >
                                <OsIcon />
                                {label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <div className="category-toolbar-actions">
                        <Select
                            value={tenant.mode ? tenant.mode : " "}
                            onValueChange={(value: string) => handleMode(value)}
                        >
                            <SelectTrigger
                                className="policy-mode-trigger"
                                aria-label="Global operating mode"
                                data-testid="select-status"
                            >
                                <span className="policy-mode-dot" data-mode={(tenant.mode || "").toLowerCase()} aria-hidden="true" />
                                <span className="policy-mode-label">Mode</span>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="AUDITING" data-testid="option-status-allowed">
                                    AUDITING
                                </SelectItem>
                                <SelectItem value="ENFORCING" data-testid="option-status-denied">
                                    ENFORCING
                                </SelectItem>
                                <SelectItem value="DISABLED" data-testid="option-status-timed">
                                    DISABLED
                                </SelectItem>
                            </SelectContent>
                        </Select>

                        <Button
                            onClick={() => handleAction("Create new profile", "new profile", -1)}
                            data-testid="button-create-profile"
                            size="sm"
                            className="category-primary-action"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create new profile
                        </Button>
                    </div>
                </div>

                <section className="policy-panel">
                    <div className="policy-panel-header">
                        <div className="policy-panel-heading">
                            <span className="category-icon" aria-hidden="true">
                                <FileCog />
                            </span>
                            <div className="min-w-0">
                                <h2 className="policy-panel-title" data-testid="text-platform-logs-title">
                                    {activeOsLabel} Profiles
                                </h2>
                                <p className="policy-panel-description">
                                    Manage security profiles and resources categories for each platform
                                </p>
                            </div>
                        </div>
                        <div className="policy-panel-tools">
                            {profilesLoaded && (
                                <span className="policy-count">
                                    {filteredProfiles.length} {filteredProfiles.length === 1 ? "profile" : "profiles"}
                                </span>
                            )}
                            <div className="policy-search">
                                <Search aria-hidden="true" />
                                <Input
                                    placeholder="Search profiles..."
                                    aria-label="Search profiles"
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    data-testid="input-search-profiles"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="policy-scroll" key={`${activeTab}-${safeCurrentPage}`}>
                        <div className="policy-table">
                            <div className="policy-table-head policy-grid">
                                <div>Profile</div>
                                <div>Default Time</div>
                                <div>Operating Mode</div>
                                <div className="text-right">Actions</div>
                            </div>

                            <TabsContent value="windows" className="policy-rows mt-0">
                                {renderProfilesList("windows", "Windows")}
                            </TabsContent>

                            <TabsContent value="linux" className="policy-rows mt-0">
                                {renderProfilesList("linux", "Linux")}
                            </TabsContent>

                            <TabsContent value="mac" className="policy-rows mt-0">
                                {renderProfilesList("mac", "macOS")}
                            </TabsContent>
                        </div>
                    </div>

                    {renderPagination()}
                </section>
            </Tabs>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="management-dialog policy-dialog sm:max-w-lg">
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon">
                                <FileCog />
                            </span>
                            <div>
                                <DialogTitle>Create New Profile</DialogTitle>
                                <DialogDescription>
                                    Clone an existing profile to start a new custom policy.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="management-dialog-body">
                        <div className="management-form-field">
                            <Label htmlFor="profileName">Profile Name</Label>
                            <Input
                                id="profileName"
                                placeholder="Enter profile name"
                                maxLength={DESCRIPTION_MAX_LENGTH}
                                onChange={(e) => setNewProfileName(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
                                value={newProfileName}
                                data-testid="input-profile-name"
                            />
                            <p className="policy-char-count">{newProfileName.length}/{DESCRIPTION_MAX_LENGTH}</p>
                        </div>

                        <div className="management-form-field">
                            <Label>Operating System</Label>
                            <RadioGroup
                                value={selectedOS}
                                onValueChange={(value) => setSelectedOS(value as typeof selectedOS)}
                                className="policy-os-options"
                            >
                                {OS_TABS.map(({ value, dialogLabel, icon: OsIcon }) => (
                                    <label key={value} htmlFor={value} className="policy-os-option">
                                        <RadioGroupItem value={value} id={value} data-testid={`radio-${value}`} />
                                        <OsIcon aria-hidden="true" />
                                        {dialogLabel}
                                    </label>
                                ))}
                            </RadioGroup>
                        </div>

                        <div className="management-form-field">
                            <Label>Clone from Profile</Label>
                            <Popover open={openCloneSelect} onOpenChange={setOpenCloneSelect}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={openCloneSelect}
                                        className="w-full justify-between font-normal"
                                        data-testid="select-clone-profile"
                                    >
                                        {selectedCloneProfile
                                            ? getAvailableProfilesForCloning().find((profile) => profile.id === Number(selectedCloneProfile))?.name
                                            : "Select profile to clone..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Search profiles..." />
                                        <CommandEmpty>No profiles found.</CommandEmpty>
                                        <CommandGroup>
                                            {getAvailableProfilesForCloning().map((profile) => (
                                                <CommandItem
                                                    key={profile.id}
                                                    value={profile.name}
                                                    onSelect={() => {
                                                        setSelectedCloneProfile(String(profile.id));
                                                        setOpenCloneSelect(false);
                                                    }}
                                                >
                                                    <Check
                                                        className={`mr-2 h-4 w-4 ${selectedCloneProfile === String(profile.id) ? "opacity-100" : "opacity-0"
                                                            }`}
                                                    />
                                                    {profile.name} ({profile.isCopied ? 'Custom' : 'Built-in'})
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <DialogFooter className="management-dialog-footer">
                        <Button
                            variant="outline"
                            onClick={() => setShowCreateDialog(false)}
                            data-testid="button-cancel-create"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateProfile}
                            disabled={!newProfileName.trim()}
                            className="endpoint-primary-action"
                            data-testid="button-confirm-create"
                        >
                            Create Profile
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
